import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import {
  LogistikCartItem,
  LogistikChancenBriefCartItem,
  LogistikLearningMaterialCartItem,
  LogistikWorkshoppackCartiItem,
} from '@chancenwerk/db/LogistikCart.js';
import { LogistikSettings } from '@chancenwerk/db/LogistikClearanceSettings.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkMaterial,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import { LogistikProductAvailability, LogistikProductCategory } from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-card.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image-carousel.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, filter, from, map, of, switchMap } from 'rxjs';
import { LogistikDialogDisallowedOrderChange } from '../components/dialogs/logistik-dialog-disallowed-order-change.js';
import { LogistikDialogItemAddedToCart } from '../components/dialogs/logistik-dialog-item-added-to-cart.js';
import '../components/logistik-component-audience-tag.js';
import '../components/logistik-component-base-header.js';
import '../components/logistik-component-base-page-layout.js';
import '../components/logistik-component-edit-chancen-brief-variables.js';
import { g_logistikCart, productClasses } from '../globalSubjects.js';
import { g_getAvailabilityStatusText, g_getProductCategoryString, reconstructCartItem } from '../helper.js';
import {
  calculateAllowedCartChange,
  isMatchingCartItem,
  type AddResult,
  type CartAddRequest,
  type ChangeResult,
  type ErrorResult,
  type NoneResult,
} from '../helpers/order-amount-range.js';

/**
 * @customElement logistik-page-product-details
 */
@customElement('logistik-page-product-details')
export class LogistikPageProductDetails extends ChemistryLitElement {
  @property() selectedProductID = new RXController(this, '');

  @state() protected _totalOrderQuantity = new RXController(this, of(0));
  @state() protected _maxOrderQuantity = new RXController(this, Promise.resolve(0));

  @state() protected readonly _selectedAmount = new RXController(this, 1);
  @state() protected readonly _participantsAmount = new RXController<Maybe<number>>(this, null);
  @state() protected readonly _trainersAmount = new RXController<Maybe<number>>(this, null);
  @state() protected readonly _setting = new RXController(this, LogistikSettings.get()(LogistikSettings.allFields));
  @state() protected readonly _currentUser = new RXController(this, currentUser());

  @state() protected readonly _product = new RXController(
    this,
    this.selectedProductID.observable.pipe(
      filter(Boolean),
      // ! `allFields` and `selectionSet(() => LogistikProduct, 1)` do not behave the same here, do not replace blindly
      switchMap(productID => getByID<typeof LogistikProduct>(LogistikProduct, productID)(LogistikProduct.allFields)),
      map(p => p as LogistikProduct),
    ),
  );
  @state() protected readonly _productType = new RXController(
    this,
    this._product.observable.pipe(
      map(product => {
        if (!product) return null;
        const productClass = productClasses[product._class];
        return productClass;
      }),
    ),
  );

  @state() protected readonly _pdfFile = new RXController(
    this,
    this._product.observable.pipe(
      switchMap(product =>
        (product instanceof AdvertisingMaterial || product instanceof LearningMaterial) && product.pdfID
          ? getByID<typeof AdornisFile>(AdornisFile, product.pdfID)(AdornisFile.allFields)
          : of(null),
      ),
    ),
  );

  @state() protected readonly _productSetProducts = new RXController(this, this._product.observable, obs =>
    obs.pipe(
      switchMap(product =>
        product instanceof LogistikProductSet && product.selectedProducts?.length
          ? getByID(
              LogistikProduct,
              product.selectedProducts.map(a => a.productID),
            )({ _id: 1, title: 1, _class: 1, imageID: 1 })
          : of(null),
      ),
    ),
  );

  @state() protected readonly _existingCartItemQuantity = new RXController(
    this,
    combineLatest([this.selectedProductID.observable, g_logistikCart.asObservable()]).pipe(
      filter(([productID, cart]) => productID !== null),
      switchMap(([productID, cart]) => {
        return of(cart.items.find(item => item.productID === productID)?.quantity ?? 0);
      }),
    ),
  );

  protected readonly _productsInCart = new RXController(this, g_logistikCart, cart$ =>
    cart$.pipe(
      switchMap(cart =>
        cart.items
          ? from(
              getByID<typeof LogistikProduct>(
                LogistikProduct,
                cart.items.map(i => i.productID),
              )(selectionSet(() => LogistikProduct, 1)),
            )
          : of([] as LogistikProduct[]),
      ),
    ),
  );

  @state() protected readonly _backRoute = new RXController<string | undefined, string | undefined>(this, undefined);

  protected _logistikCartChancenBriefItem?: LogistikCartItem = new LogistikCartItem({});

  override connectedCallback(): void {
    super.connectedCallback();

    combineLatest([this._product.observable, routingState.searchParams.asObservable()]).subscribe(([product, sps]) => {
      this._backRoute.next(
        `${
          {
            [AdvertisingMaterial._class]: LogistikRoutes.advertisingMaterials,
            [LearningMaterial._class]: LogistikRoutes.learningMaterials,
            [WorkMaterial._class]: LogistikRoutes.workMaterials,
            [EventTicket._class]: LogistikRoutes.tickets,
            [Furnishing._class]: LogistikRoutes.furnishingMaterials,
            [WorkshopPack._class]: LogistikRoutes.workshopPacks,
            [ChancenBrief._class]: LogistikRoutes.chancenBrief,
            [TravelCertificate._class]: LogistikRoutes.travelCertificate,
            [LogistikProductSet._class]: LogistikRoutes.logistikProductSets,
          }[product?._class ?? ''] ?? '/'
        }?${sps.toString()}`,
      );
    });
  }

  override render() {
    if (this._product.isLoading) return html` <cr-loading></cr-loading> `;
    if (!this._product.value)
      return html` <x-empty-placeholder .text=${'Produkt nicht gefunden'} .img=${'close'}></x-empty-placeholder> `;
    if (!this._product.value.isPublished)
      return html` <x-empty-placeholder .text=${'Produkt nicht gefunden'} .img=${'close'}></x-empty-placeholder> `;
    return html`
      <logistik-component-base-page-layout>
        ${this._header()}
        <cr-flex horizontal wrap space="md" center>
          <cr-image-carousel .imageIDs=${this._product.value.imageIDs ?? [this._product.value.imageID]}>
          </cr-image-carousel>
          <cr-flex space-between space="xl" flex ${css({ margin: `${this.spacing.md} 0`, minWidth: '300px' })}>
            <cr-flex space="md">
              <cr-flex space="sm" ${css({ marginBottom: this.spacing.md })}>
                <cr-h3> ${this._product.value.title} </cr-h3>
              </cr-flex>
              ${this._metadataDisplayText()}
              <cr-card> <cr-text> ${unsafeHTML(this._product.value.description)} </cr-text> </cr-card>
              ${this._pdfPreviewButton()} ${this._targetAudienceSelector()}
            </cr-flex>
            ${this._chancenBriefVariables()}
            <cr-flex space="xs">
              ${this._numericalInputFields()} ${this._cartQuantityText()} ${this._addToCartButtons()}
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }

  protected _metadataDisplayText(): Renderable {
    return html`
      <cr-flex space="sm">
        <cr-flex horizontal space="xs" crossaxis-center>
          <cr-icon ${css({ fontSize: '1.2rem' })}> local_shipping </cr-icon>
          ${g_getAvailabilityStatusText(this._product.value?.availability ?? LogistikProductAvailability.UNAVAILABLE)}
        </cr-flex>
        ${this._product.value instanceof LearningMaterial && this._product.value.topic
          ? html`
              <cr-flex horizontal space="sm">
                <cr-text bold>Thema:</cr-text>
                <cr-text> ${this._product.value.topic} </cr-text>
              </cr-flex>
            `
          : nothing}
        ${this._product.value instanceof LearningMaterial && this._product.value.grades?.at(0)
          ? html`
              <cr-flex horizontal space="sm">
                <cr-text bold>Jahrgangsstufen:</cr-text>
                <cr-text> ${this._product.value.grades.at(0)} </cr-text>
              </cr-flex>
            `
          : nothing}
        ${this._product.value instanceof LogistikProductSet && this._productSetProducts.value?.length
          ? html`
              <cr-accordeon
                .items=${[
                  {
                    title: 'Enthaltene Produkte',
                    text: html`
                      <cr-flex space="md">
                        ${this._productSetProducts.value.map(
                          sp =>
                            html`
                              <logistik-component-simple-product-item
                                .product=${sp}
                                .quantity=${(this._product.value as LogistikProductSet).selectedProducts.find(
                                  a => a.productID === sp._id,
                                )!.quantity}
                              >
                              </logistik-component-simple-product-item>
                            `,
                        )}
                      </cr-flex>
                    `,
                  },
                ]}
              >
              </cr-accordeon>
            `
          : nothing}
      </cr-flex>
    `;
  }

  protected _pdfPreviewButton(): Renderable {
    if (!this._pdfFile.value) return nothing;
    return html`
      <cr-button
        mode="outline"
        icon="remove_red_eye"
        ${css({ alignSelf: 'flex-start' })}
        @click=${() =>
          goTo(
            `${this._pdfFile.value!.getServeLink()}/${this._pdfFile.value?.meta?.fileName}.${
              this._pdfFile.value?.meta?.extension
            }`,
            { target: '_blank' },
          )}
      >
        PDF-Vorschau
      </cr-button>
    `;
  }

  protected _targetAudienceSelector(): Renderable {
    if (!(this._product.value instanceof AdvertisingMaterial)) return nothing;
    return html`
      <cr-flex space="sm">
        <cr-text bold>Zielgruppen:</cr-text>
        <cr-flex horizontal wrap space="sm">
          ${(this._product.value.targetAudiences ?? []).map(
            targetAudience =>
              html` <logistik-component-audience-tag .audience=${targetAudience}></logistik-component-audience-tag> `,
          )}
        </cr-flex>
      </cr-flex>
    `;
  }

  protected _numericalInputFields(): Renderable {
    return html`
      ${this._product.value instanceof WorkshopPack
        ? html`
            <cr-input
              placeholder="Anzahl der Teilnehmenden:"
              placeholder-mode="prefix"
              type="number"
              .value=${this._participantsAmount}
              ${css({ width: '100%' })}
              @value-picked=${(event: CustomEvent) => {
                if (event.detail.value <= 0) {
                  this._participantsAmount.next(1);
                  ChromiumSnackbar.show('Mindest Anzahl der Teilnehmenden wurde nicht erreicht');
                  return;
                }
                this._participantsAmount.next(event.detail.value);
              }}
            ></cr-input>
            <cr-input
              placeholder="Anzahl der Trainer:innen:"
              placeholder-mode="prefix"
              type="number"
              .value=${this._trainersAmount}
              ${css({ width: '100%' })}
              @value-picked=${(event: CustomEvent) => {
                if (event.detail.value <= 0) {
                  this._trainersAmount.next(1);
                  void ChromiumSnackbar.show('Mindest Anzahl der Trainer:innen wurde nicht erreicht');
                  return;
                }
                this._trainersAmount.next(event.detail.value);
              }}
            ></cr-input>
          `
        : nothing}
      ${this._product.value instanceof WorkshopPack
        ? nothing
        : html`<cr-input
            placeholder="Bestellmenge:"
            placeholder-mode="prefix"
            type="number"
            .value=${this._selectedAmount.value}
            label="Menge"
            @value-picked=${(event: CustomEvent<{ value: number }>) => this._selectedAmount.next(event.detail.value)}
            ${css({ width: '100%' })}
          ></cr-input>`}
    `;
  }

  protected _cartQuantityText(): Renderable {
    return html`
      <cr-text ${css({ color: '#777', marginBottom: this.spacing.md, fontSize: '0.9rem' })}>
        ${this._existingCartItemQuantity.value
          ? html` (${this._existingCartItemQuantity.value} im Warenkorb)`
          : nothing}
      </cr-text>
    `;
  }

  protected _addToCartButtons(): Renderable {
    return html`
      <cr-flex horizontal space="md" space-between>
        <cr-button
          ?disabled=${this._product.value?.availability === LogistikProductAvailability.UNAVAILABLE ||
          this._selectedAmount.value <= 0}
          icon="add_shopping_cart"
          ${css({ flex: 20 })}
          @click=${async () => await this._onAddToCartClicked()}
        >
          Zur Bestellung hinzufügen
        </cr-button>
        ${this._additionalCartButton()}
      </cr-flex>
    `;
  }

  protected _additionalCartButton(): Renderable {
    if (this._product.value instanceof ChancenBrief)
      return html`
        <cr-button icon="print" ${css({ flex: 11 })} @click=${() => this._onAddToCartClicked(true)}>
          Selbstdruck
        </cr-button>
      `;
    return nothing;
  }

  protected _header() {
    if (!this._product.value) return nothing;
    return html`
      <logistik-component-base-header
        pageTitle=${`${this._productType.value}: ${this._product.value.title}`}
        backRoute=${this._backRoute.value ?? '/'}
      ></logistik-component-base-header>
    `;
  }

  protected _chancenBriefVariables() {
    if (this._product.value instanceof ChancenBrief || this._product.value instanceof TravelCertificate)
      return html`
        <cr-card>
          <x-flex space="md">
            <cr-text bold> ${g_getProductCategoryString(this._product.value._class)} </cr-text>
            <logistik-component-edit-chancen-brief-variables
              .product=${this._product.value}
              .orderItem=${this._logistikCartChancenBriefItem}
              @value-picked=${(e: ValueEvent<LogistikCartItem>) =>
                (this._logistikCartChancenBriefItem = e.detail.value)}
            ></logistik-component-edit-chancen-brief-variables>
          </x-flex>
        </cr-card>
      `;
    return nothing;
  }

  protected async _onAddToCartClicked(isItemDigital: boolean = false): Promise<void> {
    if (this._product.value?.notice && this._product.value?.notice.length > 0) {
      if (
        !(await ChromiumDialog.confirm(html`<cr-flex space="md">
          <cr-text bold>Hinweis</cr-text>
          <cr-text>${this._product.value.notice}</cr-text>
          <cr-text> Willst du das Produkt trotzdem in den Warenkorb legen? </cr-text>
        </cr-flex>`))
      )
        return;
    }

    const isWorkshop = this._product.value instanceof WorkshopPack;

    if (isWorkshop && !this._participantsAmount.value) {
      void ChromiumSnackbar.show(`Bitte wählen Sie die Anzahl der Teilnehmenden`);
      return;
    }

    if (isWorkshop && !this._trainersAmount.value) {
      void ChromiumSnackbar.show('Bitte wählen Sie die Anzahl der Trainer:innen');
      return;
    }

    if (this._selectedAmount.value <= 0) return;

    const request: CartAddRequest = {
      type: 'add',
      cart: g_logistikCart.value,
      quantity: this._selectedAmount.value,
      productsInCart: this._productsInCart.value,
      product: this._product.value,
      tokenMaxOrderQuantity: this._maxOrderQuantity.value,
      isDigital: isItemDigital,
      trainersQuantity: this._trainersAmount.value,
      participantsQuantity: this._participantsAmount.value,
      chancenBriefCartItem: this._logistikCartChancenBriefItem,
    };
    const allowedChange = calculateAllowedCartChange(request);

    switch (allowedChange.type) {
      case 'error':
        this._handleErrorResult(request, allowedChange);
        return;
      case 'change':
        this._handleChangeResult(request, allowedChange);
        return;
      case 'none':
        await this._handleNoneResult(allowedChange);
        return;
      case 'add':
        if (allowedChange.quantity === 0) {
          await this._handleNoneResult(allowedChange);
          return;
        }
        const r = await this._handleAddResult(isItemDigital, allowedChange);
        if (!r) return;
      // ! Fallthrough
      case 'redundant':
        LogistikDialogItemAddedToCart.showPopup({
          props: {
            product: this._product.value!,
            quantity: this._selectedAmount.value,
            backRoute: this._backRoute.value,
          },
        });
        void ChromiumSnackbar.show('Zur Bestellung hinzugefügt');
        this.requestUpdate();
        break;
    }
  }

  private _handleErrorResult(request: CartAddRequest, allowedChange: ErrorResult) {
    logger.error(
      {
        cartItems: g_logistikCart.value.items.map(v => v.productID),
        productID: this._product.value?._id,
        cartAddRequest: request,
      },
      allowedChange.reason,
    );
    void ChromiumDialog.alert(
      'Es gab ein Problem beim Hinzufügen dieses Produktes, bitte wenden Sie sich an die CHANCENlogistik',
    );
  }

  private _handleChangeResult(request: CartAddRequest, allowedChange: ChangeResult) {
    logger.error(
      {
        cartItems: g_logistikCart.value.items.map(v => v.productID),
        productID: this._product.value?._id,
        cartAddRequest: request,
      },
      `allowedChange.type should be 'add' but is ${allowedChange.type}`,
    );
    void ChromiumDialog.alert(
      'Es gab ein Problem beim Ändern des Warenkorbs, bitte wenden Sie sich an die CHANCENlogistik',
    );
  }

  protected async handleDisallowedChange(allowedChange: NoneResult | AddResult) {
    return await LogistikDialogDisallowedOrderChange.showPopup({
      props: {
        allowedChange,
        product: this._product.value!,
      },
    }).catch(() => false);
  }

  private async _handleNoneResult(allowedChange: NoneResult | AddResult) {
    await this.handleDisallowedChange(allowedChange);
  }

  private async _handleAddResult(isItemDigital: boolean, allowedChange: AddResult) {
    if (!allowedChange.accepted) {
      const isOrderFixQuantity = await this.handleDisallowedChange(allowedChange);
      if (isOrderFixQuantity) this._selectedAmount.next(allowedChange.quantity);
      else return false;
    }

    const matchingCartItem = g_logistikCart.value.items.find(ci =>
      isMatchingCartItem(ci, {
        product: this._product.value,
        trainersQuantity: this._trainersAmount.value,
        participantsQuantity: this._participantsAmount.value,
        isDigital: isItemDigital,
        chancenBriefCartItem: this._logistikCartChancenBriefItem,
      }),
    );

    if (!matchingCartItem) this.addProductToNewCartItem(isItemDigital, allowedChange.quantity);
    else this.addQuantityToMatchingCartItem(matchingCartItem, allowedChange.quantity);
    return true;
  }

  private addProductToNewCartItem(isDigital: boolean, quantity: number) {
    const productClass = Object.entries(productClasses).find(([_, value]) => value === this._productType.value)?.[0];
    const productCategory = productClass
      ? {
          [AdvertisingMaterial._class]: LogistikProductCategory.ADVERTISING_MATERIAL,
          [LearningMaterial._class]: LogistikProductCategory.LEARNING_MATERIAL,
          [WorkMaterial._class]: LogistikProductCategory.WORK_MATERIAL,
          [EventTicket._class]: LogistikProductCategory.TICKETS,
          [Furnishing._class]: LogistikProductCategory.FURNISHING,
          [ChancenBrief._class]: LogistikProductCategory.CHANCEN_BRIEF,
          [TravelCertificate._class]: LogistikProductCategory.TRAVEL_CERTIFICATE,
          [WorkshopPack._class]: LogistikProductCategory.WORKSHOP_PACK,
          [LogistikProductSet._class]: LogistikProductCategory.SET,
        }[productClass]
      : undefined;

    const item =
      productClass === WorkshopPack._class
        ? new LogistikWorkshoppackCartiItem({
            ...(this._logistikCartChancenBriefItem?.toObject() ?? {}),
            productID: this._product.value!._id,
            quantity,
            trainersQuantity: this._trainersAmount.value,
            participantsQuantity: this._participantsAmount.value,
            productCategory,
          })
        : productClass === LearningMaterial._class
        ? new LogistikLearningMaterialCartItem({
            ...(this._logistikCartChancenBriefItem?.toObject() ?? {}),
            productID: this._product.value!._id,
            quantity,
            productCategory,
            isDigital,
          })
        : productClass === ChancenBrief._class
        ? new LogistikChancenBriefCartItem({
            ...(this._logistikCartChancenBriefItem?.toObject() ?? {}),
            productID: this._product.value!._id,
            quantity,
            productCategory,
            isSelfPrint: isDigital,
          })
        : new LogistikCartItem({
            ...(this._logistikCartChancenBriefItem?.toObject() ?? {}),
            productID: this._product.value!._id,
            quantity,
            productCategory,
          });
    g_logistikCart.value.items = [...g_logistikCart.value.items, item];
    g_logistikCart.next(g_logistikCart.value);
  }

  private addQuantityToMatchingCartItem(matchingCartItem: LogistikCartItem, addedQuantity: number) {
    g_logistikCart.value.items = g_logistikCart.value.items.map(cartItem =>
      cartItem._id === matchingCartItem._id ? reconstructCartItem(matchingCartItem, addedQuantity) : cartItem,
    );
    g_logistikCart.next(g_logistikCart.value);
  }
}
